// Narrow compatibility adapters for the parked prototype screens. They let
// the existing JSX render canonical API resources while the forms are migrated
// to typed payloads, without teaching those mock-heavy screens two data shapes.

import type { Campaign, Production } from '@/api/types';
import { fmtDate } from '@/lib/datetime';

/** Rendered where a venue name belongs and none is known. It lives here, beside
 *  the adapter, so it stays a RENDER-time placeholder: putting it on the
 *  production object is what let `metaDefaults` interpolate "Venue TBD" — and
 *  before that a bare uuid — into published ad copy. */
export const VENUE_UNKNOWN = 'Venue TBD';

export interface LegacyManageProduction {
  id: string;
  title: string;
  /** The venue's NAME, or null when it is not known. Never an id: this field
   *  is read by screens that put it in front of people, and it used to carry
   *  `venue_id`. */
  venue: string | null;
  genre: string;
  dates: string;
  avgPrice: number;
  capacity: number;
  posterBg: string;
  posterFg: string;
  poster: string;
  tag: string | null;
  heat: number;
  weekGross: number;
}

export interface LegacyManageCampaign {
  id: string;
  name: string;
  production: string;
  channel: Campaign['channel'];
  status: Campaign['status'];
  operational_status?: Campaign['operational_status'];
  /** Carried, not dropped: the campaign header's armed pill — and the ONLY disarm
   *  control in the product — is gated on it. It was absent here once, which silently
   *  removed the operator's ability to cancel an auto-launch. */
  auto_launch_requested_at: string | null;
  budget: number;
  spent: number;
  days: number;
  starred: boolean;
  audienceId: string;
  startDate: string;
  endDate: string;
  markup: boolean;
  flights: Array<{
    id: string;
    name: string;
    start: string;
    end: string;
    budget: number;
  }>;
}

function stableHue(value: string): number {
  let hash = 0;
  for (const char of value) hash = ((hash * 31) + char.charCodeAt(0)) | 0;
  return Math.abs(hash) % 360;
}

function shortDate(value: string | null): string | null {
  if (!value) return null;
  const formatted = fmtDate(value);
  return formatted === '—' ? null : formatted;
}

/** `venueNames` maps venue id -> name. It is optional only so a caller that has
 *  no use for the venue need not fetch it; a caller that DISPLAYS the venue must
 *  pass it, because the alternative used to be showing a uuid. */
export function adaptProductionForLegacyManage(
  production: Production,
  venueNames?: ReadonlyMap<string, string | null>,
): LegacyManageProduction {
  const hue = stableHue(production.id);
  const start = shortDate(production.first_event_starts_at);
  const end = shortDate(production.last_event_starts_at);
  const capacity = production.seated_capacity && production.seated_capacity > 0
    ? (production.seated_sold ?? 0) / production.seated_capacity
    : 0;

  return {
    id: production.id,
    title: production.name,
    // This used to be `production.venue_id ?? 'Venue TBD'` — a display
    // placeholder. `metaDefaults` interpolated it into ad copy, so a creative
    // reached Meta reading "Limited engagement at the f2d4c6b3-8ea9-…". Resolve
    // the name here or admit we do not have it; callers render their own
    // placeholder for null. The name is never invented.
    venue: (production.venue_id ? venueNames?.get(production.venue_id) : null) ?? null,
    genre: production.genre ?? 'Uncategorized',
    dates: start && end ? `${start} – ${end}` : start ?? end ?? 'Dates TBD',
    avgPrice: Math.round((production.avg_price_cents ?? 0) / 100),
    capacity: Math.max(0, Math.min(1, capacity)),
    posterBg: `oklch(42% 0.12 ${hue})`,
    posterFg: 'oklch(94% 0.03 85)',
    poster: production.name,
    tag: production.tag,
    heat: production.heat ?? 0,
    weekGross: (production.week_gross_cents ?? 0) / 100,
  };
}

export function adaptCampaignForLegacyManage(
  campaign: Campaign,
): LegacyManageCampaign {
  const start = new Date(`${campaign.start_date}T00:00:00Z`).getTime();
  const end = new Date(`${campaign.end_date}T00:00:00Z`).getTime();
  const days = Number.isFinite(start) && Number.isFinite(end)
    ? Math.max(1, Math.round((end - start) / 86_400_000))
    : 1;

  return {
    id: campaign.id,
    name: campaign.name,
    production: campaign.production_id,
    channel: campaign.channel,
    status: campaign.status,
    operational_status: campaign.operational_status,
    auto_launch_requested_at: campaign.auto_launch_requested_at ?? null,
    budget: campaign.budget_cents / 100,
    spent: campaign.spent_cents / 100,
    days,
    starred: campaign.starred,
    audienceId: campaign.audience_id,
    startDate: campaign.start_date,
    endDate: campaign.end_date,
    markup: campaign.markup_on,
    flights: campaign.flights.map((flight) => ({
      id: flight.id,
      name: flight.name,
      start: flight.start_date,
      end: flight.end_date,
      budget: flight.budget_cents / 100,
    })),
  };
}
